import type { InputHTMLAttributes, ReactNode } from 'react';
import { MaterialIcon } from './MaterialIcon';

interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  icon?: string;
  wrapperClassName?: string;
}

export function TextInput({ label, icon, wrapperClassName = '', className = '', id, ...props }: TextInputProps) {
  const inputId = id ?? label.toLowerCase().replace(/\s+/g, '-');
  return (
    <div className={`flex flex-col gap-stack-sm ${wrapperClassName}`}>
      <label htmlFor={inputId} className="font-label-bold text-label-bold text-primary">
        {label}
      </label>
      <div className="relative neon-glow-primary rounded-lg transition-all duration-200">
        <input
          id={inputId}
          className={`w-full bg-surface-container-lowest/50 border border-white/10 rounded-lg px-4 py-3 outline-none text-on-surface placeholder:text-on-surface-variant/40 focus:border-primary transition-colors ${className}`}
          {...props}
        />
        {icon && (
          <MaterialIcon
            name={icon}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-on-surface-variant/40 pointer-events-none"
          />
        )}
      </div>
    </div>
  );
}

interface SelectFieldProps {
  label: string;
  children: ReactNode;
  className?: string;
  value?: string | number;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

export function SelectField({ label, children, className = '', value, onChange }: SelectFieldProps) {
  const selectId = label.toLowerCase().replace(/\s+/g, '-');
  return (
    <div className={`flex flex-col gap-stack-sm ${className}`}>
      <label htmlFor={selectId} className="text-on-surface-variant">
        {label}
      </label>
      <select
        id={selectId}
        value={value}
        onChange={onChange}
        className="w-full bg-surface-container-lowest/50 border border-white/10 rounded-lg px-4 py-3 outline-none text-on-surface appearance-none focus:border-primary"
      >
        {children}
      </select>
    </div>
  );
}

interface ToggleFieldProps {
  label: string;
  description?: string;
  defaultChecked?: boolean;
}

export function ToggleField({ label, description, defaultChecked }: ToggleFieldProps) {
  const toggleId = label.toLowerCase().replace(/\s+/g, '-');
  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-col">
        <label htmlFor={toggleId} className="text-on-surface">
          {label}
        </label>
        {description && <span className="text-xs text-on-surface-variant">{description}</span>}
      </div>
      <label className="relative inline-flex items-center cursor-pointer">
        <input id={toggleId} type="checkbox" className="sr-only peer" defaultChecked={defaultChecked} />
        <div className="w-11 h-6 bg-surface-container-highest peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-secondary" />
      </label>
    </div>
  );
}
